import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '@/api';
import type { ReviewSubmission, RewardTaskType } from '@/types';

export interface ReviewSubmissionsState {
  submissions: ReviewSubmission[];
  /**
   * Most recent submission per task type. We only ever care about the
   * latest one for status display: a rejected one is replaced as soon as
   * the user resubmits.
   */
  latestByTask: Partial<Record<RewardTaskType, ReviewSubmission>>;
  /**
   * Convenience flag: returns true if the user has at least one approved
   * submission across any task — used to celebrate the pro upgrade.
   */
  hasAnyApproved: boolean;
  totalApprovedCredits: number;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useReviewSubmissions(userId?: string | null): ReviewSubmissionsState {
  const [submissions, setSubmissions] = useState<ReviewSubmission[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.listUserSubmissions(userId);
      setSubmissions(data);
    } catch (e) {
      console.error('Failed to load review submissions', e);
      setError(e instanceof Error ? e.message : 'Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const latestByTask = useMemo(() => {
    const map: Partial<Record<RewardTaskType, ReviewSubmission>> = {};
    const ordered = [...submissions].sort((a, b) =>
      (b.created_at || '').localeCompare(a.created_at || '')
    );
    for (const s of ordered) {
      if (!map[s.task_type]) {
        map[s.task_type] = s;
      }
    }
    return map;
  }, [submissions]);

  const hasAnyApproved = useMemo(
    () => submissions.some((s) => s.status === 'approved'),
    [submissions]
  );

  const totalApprovedCredits = useMemo(
    () =>
      submissions
        .filter((s) => s.status === 'approved')
        .reduce((sum, s) => sum + Number(s.reward_credits || 0), 0),
    [submissions]
  );

  return {
    submissions,
    latestByTask,
    hasAnyApproved,
    totalApprovedCredits,
    isLoading,
    error,
    refresh,
  };
}
